import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { useFirebase } from '../context/FirebaseContext.js';
import OfflineIndicator from './OfflineIndicator.js';
import { isConnectivityError, resolveConnectivityError } from '../utils/firebaseErrorHandler.js';

/**
 * ProtectedRoute Component
 * 
 * This component wraps routes that require authentication.
 * It redirects to login if user is not authenticated.
 * It handles offline access for already authenticated users.
 * It shows a loading state while auth state is being determined.
 */
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading, authInitialized, authError } = useAuth();
  const { connection, isOnline } = useFirebase();
  const location = useLocation();
  const [isResolving, setIsResolving] = useState(false);
  const [hasResolved, setHasResolved] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // Handle connectivity issues
  useEffect(() => {
    const handleConnectivityIssue = async () => {
      // Only try to resolve if we're offline/disconnected and haven't already tried
      if ((!isOnline || !connection?.isConnected) && !hasResolved && !isResolving) {
        setIsResolving(true);
        
        try {
          await resolveConnectivityError();
          setHasResolved(true);
        } catch (error) {
          setResolveError(error);
          console.error('Failed to resolve connectivity issue:', error);
        } finally {
          setIsResolving(false);
        }
      }
    };
    
    // Only attempt to resolve connectivity issues if auth is already initialized
    if (authInitialized) {
      handleConnectivityIssue();
    }
  }, [isOnline, connection, hasResolved, isResolving, authInitialized]);

  // Show loading state if auth is not initialized or explicitly loading
  if (!authInitialized || loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Handle auth errors
  if (authError && !isConnectivityError(authError)) {
    return (
      <div className="error-container">
        <h2>Authentication Error</h2>
        <p>{authError}</p>
        <button onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  // Handle offline mode - if the user was previously authenticated locally
  // but we can't verify with Firebase due to being offline
  const isOfflineAuthenticated = !isOnline && currentUser;
  
  // Redirect to login if not authenticated
  if (!currentUser && !isOfflineAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show connectivity issue screen if we can't connect to Firebase
  const hasConnectivityIssue = (!isOnline || !connection?.isConnected) && isConnectivityError(authError);
  
  if (hasConnectivityIssue && !hasResolved) {
    return (
      <OfflineIndicator 
        isResolving={isResolving} 
        error={resolveError || authError} 
        isAuthenticated={!!currentUser} 
      />
    );
  }

  // User is authenticated, render the protected content
  return children;
};

export default ProtectedRoute; 